import {
  Card,
  Parameters,
  Actions,
  Action,
  Attributes,
  AttributesRow,
  Parameter,
  Flag,
  FlagProps,
} from 'platform/components';
import {HStack, VStack, Space, Show, Text, Hide} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNonEmptyArray, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, useToggle, buildArray, DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {ActionParameters} from '../ActionParameters/ActionParameters';

interface InfoCardProps extends TestIdProps {
  title: string | Nullish;
  subTitle?: string | Nullish;
  description?: Parameter[] | Nullish;
  status?: Parameter[] | Nullish;
  actions?: Action[];
  attributes?: AttributesRow[];
  isOpenByDefault?: boolean;
  shouldShowActionParameters?: boolean;
  customerId?: string;
  selectedContactId?: string;
  flags?: FlagProps[];
  titleSuffix?: ReactNode;
  isPrimary?: boolean;
}

export function InfoCard(props: InfoCardProps) {
  const [isOpen, toggleOpen] = useToggle(props.isOpenByDefault);

  const actions = buildArray<Action>(props.actions).when(isNonEmptyArray(props.attributes), {
    type: 'iconButton',
    icon: isOpen ? 'navigation/expand_less' : 'navigation/expand_more',
    onClick: toggleOpen,
  });

  return (
    <Card variant="inlineWhite">
      <HStack align="flex-start">
        <VStack minWidth={0}>
          <HStack spacing={1} align="center">
            <Text size="small" alternative data-testid={suffixTestId('title', props)}>
              {props.title}
            </Text>
            <Show when={isNotNilOrEmpty(props.subTitle)}>
              <Text color="tertiary" size="small">
                {DOT_CHARACTER}
              </Text>
              <Text size="small" data-testid={suffixTestId('subTitle', props)}>
                {props.subTitle}
              </Text>
            </Show>
            <Show when={props.isPrimary}>
              <Flag
                size="small"
                colorScheme="green"
                label={i18n.t('entity.customer.labels.contactPersonsHelperText')}
                data-testid={suffixTestId('primaryFlag', props)}
              />
            </Show>
            <Show when={isNonEmptyArray(props.flags)}>
              {props.flags?.map((flag) => <Flag key={flag.label} size="small" {...flag} />)}
            </Show>
            {props.titleSuffix}
          </HStack>
          <HStack spacing={1}>
            <Show when={isNotNilOrEmpty(props.description)}>
              <Show when={props.shouldShowActionParameters}>
                <ActionParameters
                  parameters={props.description}
                  customerId={props.customerId}
                  color="secondary"
                  size="small"
                  tooltipLabelSms={i18n.t('general.actions.sendMessage')}
                  tooltipLabelEmail={i18n.t('general.actions.sendEmail')}
                  data-testid={suffixTestId('description', props)}
                />
              </Show>
              <Hide when={props.shouldShowActionParameters}>
                <Parameters
                  parameters={props.description}
                  color="secondary"
                  size="small"
                  data-testid={suffixTestId('description', props)}
                />
              </Hide>
            </Show>
            <Show when={isNotNilOrEmpty(props.status)}>
              <Show when={isNotNilOrEmpty(props.status) && isNotNilOrEmpty(props.description)}>
                <Text color="tertiary" size="small">
                  {DOT_CHARACTER}
                </Text>
              </Show>
              <Parameters
                parameters={props.status}
                color="danger"
                size="small"
                data-testid={suffixTestId('status', props)}
              />
            </Show>
          </HStack>
        </VStack>
        <Space fillAvailable />
        <Actions size="small" actions={actions} data-testid={suffixTestId('actions', props)} />
      </HStack>
      <Show when={isOpen && isNonEmptyArray(props.attributes)}>
        <Space vertical={4} />
        <Attributes rows={props.attributes} size="third" data-testid={props['data-testid']} />
      </Show>
    </Card>
  );
}
